import { formatCurrency, getCurrencySymbol } from "@angular/common";
import { DEFAULT_CURRENCY_CODE, Directive, ElementRef, HostListener, Input, LOCALE_ID, forwardRef, inject } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { LogInvocation, LogLevel } from "@tylertech/logging";

export interface LgdCurrencyConfig
{
    precision?: number;
    scale?: number;
}

const onlyNumericRegex = /[^\d.-]/g;

@Directive({
    selector: "input[lgdCurrency]",
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LgdCurrencyValueAccessor),
            multi: true,
        }
    ]
})
export class LgdCurrencyValueAccessor implements ControlValueAccessor
{
    private readonly locale = inject(LOCALE_ID);
    private currencyCode = inject(DEFAULT_CURRENCY_CODE);
    private readonly elementRef = inject<ElementRef<HTMLInputElement>>(ElementRef);

    private hasFocus: boolean;

    private _value: string | null;

    private _onChange: (value: number) => void;

    private _onTouched: () => void;

    @Input()
    readonly lgdCurrency: Readonly<LgdCurrencyConfig>;

    @Input("value")
    get value(): string | null
    {
        return this._value;
    }

    set value(value: string | null)
    {
        this.writeValue(this._value);
    }

    @HostListener("input", ["$event"])
    onInput($event: Event): void
    {
        this.fireOnChange(($event.target as HTMLInputElement).value);
    }

    @HostListener("blur")
    _onBlur(): void
    {
        // add formatting to element.
        this.format(this.elementRef.nativeElement.value);
        this.fireOnTouched();
        this.hasFocus = false;
    }

    @HostListener("focus")
    onFocus(): void
    {
        if (this.hasFocus)
        {
            return;
        }
        // remove formatting from element.
        this.unFormat(this.elementRef.nativeElement.value);
        this.hasFocus = true;
        this.elementRef.nativeElement.select();
    }

    /** @override ControlValueAccessor.writeValue */
    writeValue(value: string): void
    {
        this._value = value;
        this.format(value);
    }

    /** @override ControlValueAccessor.registerOnChange */
    registerOnChange(fn: (value: unknown) => void): void
    {
        this._onChange = fn;
    }

    /** @override ControlValueAccessor.registerOnTouched */
    registerOnTouched(fn: () => void): void
    {
        this._onTouched = fn;
    }

    private fireOnChange(value: string): void
    {
        if (!this._onChange) return;
        this._onChange(parseFloat(value.replace(/,/g, "")));
    }

    private fireOnTouched(): void
    {
        if (!this._onTouched) return;
        this._onTouched();
    }

    @LogInvocation(LogLevel.DEBUG)
    private format(value: string | number): void
    {
        const num = value
            ? typeof value === "string"
                ? Number(value.replace(onlyNumericRegex, ""))
                : value
            : undefined;

        const currencySymbol = getCurrencySymbol(this.currencyCode, "narrow", this.locale);

        /*
         * TODO - the default is equivalent to '1.2-2'; as such this pipe will truncate the value to only two decimal places.
         * https://v17.angular.io/api/common/formatCurrency
         */
        const digitsInfo = !this.lgdCurrency.scale ? undefined : `1.2-${this.lgdCurrency.scale}`;
        // ! Explicity testing for `undefined` and `null` instead of relying on truthy eval because 0 is a valid value in this case.
        const hasValue = value !== undefined && value !== null;
        this.elementRef.nativeElement.value = hasValue
            ? formatCurrency(num, this.locale, currencySymbol, this.currencyCode, digitsInfo)
            : "";
    }

    private unFormat(value: string): void
    {
        this.elementRef.nativeElement.value = value ? value.replace(onlyNumericRegex, "") : "";
    }

    setDisabledState(isDisabled: boolean): void
    {
        this.elementRef.nativeElement.disabled = isDisabled;
    }
}
